import React from "react";
import {
  FaCheck,
  FaRocket,
  FaServer,
  FaUsers,
  FaCalendarAlt,
  FaMapMarkedAlt,
  FaShieldAlt,
  FaHandshake,
  FaGift,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";

const FEATURES = [
  "Un site moderne dédié à la vie associative de votre commune",
  "Un annuaire complet des associations avec fiches détaillées",
  "Des actualités, événements et albums photos pour valoriser les activités locales",
  "Une carte interactive des associations et infrastructures",
  "Des formulaires de contact pour faciliter les échanges avec les habitants",
  "Un espace administrateur simple pour la mairie",
  "Un accès dédié pour les responsables d’associations",
  "L’hébergement de l’application inclus dans l’abonnement",
  "Les mises à jour techniques incluses",
  "Un accompagnement à la mise en route",
];

const HIGHLIGHTS = [
  {
    icon: FaUsers,
    title: "Pour les communes",
    text: "Une solution pensée pour les mairies qui souhaitent mieux présenter leurs associations aux habitants.",
  },
  {
    icon: FaCalendarAlt,
    title: "Vie associative",
    text: "Actualités, événements, photos, contacts et informations pratiques sont regroupés au même endroit.",
  },
  {
    icon: FaMapMarkedAlt,
    title: "Carte interactive",
    text: "Les habitants retrouvent rapidement une association, un lieu de pratique ou une infrastructure municipale.",
  },
  {
    icon: FaShieldAlt,
    title: "Gestion simple",
    text: "La mairie garde la maîtrise du site, tout en permettant aux associations de mettre à jour leurs informations.",
  },
];

export default function AppPricing() {
  return (
    <section className="bg-slate-100 py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Tarif"
          title="Une formule unique, "
          highlight="simple et tout compris"
          subtitle="Un tarif clair, sans option compliquée, pour permettre à chaque commune de valoriser facilement son tissu associatif."
        />

        <div className="grid items-stretch gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal>
            <article className="relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-primary bg-slate-950 p-7 text-white shadow-2xl shadow-slate-900/20 sm:p-9">
              <div className="absolute right-0 top-0 h-40 w-40 rounded-bl-full bg-primary/20" />
              <div className="absolute bottom-0 left-0 h-32 w-32 rounded-tr-full bg-white/5" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wide text-white">
                  <FaRocket />
                  Formule annuelle
                </div>

                <h3 className="mt-6 text-3xl font-bold sm:text-4xl">
                  MonAppliAsso
                </h3>

                <p className="mt-4 max-w-2xl text-base leading-7 text-white/75">
                  Offrez à vos habitants un espace clair, moderne et facile à
                  consulter pour découvrir les associations, les événements, les
                  lieux de pratique et les contacts utiles de votre commune.
                </p>

                <div className="mt-8 rounded-[1.75rem] border border-white/10 bg-white/10 p-6">
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-white/55">
                    Abonnement tout compris
                  </p>

                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <span className="text-5xl font-bold sm:text-6xl">
                      260€
                    </span>
                    <span className="pb-2 text-lg font-bold text-white/70">
                      / an
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-white/65">
                    Soit environ 21,67 € par mois, hébergement inclus.
                  </p>
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-950">
                    <FaServer className="text-primary" />
                    Hébergement inclus
                  </span>

                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white ring-1 ring-white/10">
                    Sans option complexe
                  </span>

                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white ring-1 ring-white/10">
                    Sans frais cachés
                  </span>
                </div>
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.05}>
            <article className="flex h-full flex-col rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm dark:border-white/10 dark:bg-slate-950 sm:p-9">
              <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
                Ce qui est inclus
              </h3>

              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                Vous disposez d’une solution prête à l’emploi pour présenter et
                gérer simplement la vie associative de votre commune.
              </p>

              <ul className="mt-7 grid gap-3">
                {FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] text-white">
                      <FaCheck />
                    </span>

                    <span className="text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((item, index) => {
            const Icon = item.icon;

            return (
              <Reveal key={item.title} delay={0.08 + index * 0.04}>
                <article className="h-full rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-950">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon />
                  </div>

                  <h4 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                    {item.title}
                  </h4>

                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {item.text}
                  </p>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.18}>
          <div className="mt-6 overflow-hidden rounded-[2rem] border border-primary/20 bg-white shadow-sm dark:border-primary/30 dark:bg-slate-950">
            <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="flex items-center gap-4 bg-primary p-6 text-white sm:p-7">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-2xl">
                  <FaGift />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">
                    Programme de parrainage
                  </p>
                  <h3 className="mt-1 text-2xl font-bold">
                    10% de réduction
                  </h3>
                </div>
              </div>

              <div className="p-6 sm:p-7">
                <div className="flex items-start gap-4">
                  <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary sm:flex">
                    <FaHandshake />
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-slate-950 dark:text-white">
                      Recommandez MonAppliAsso à une autre commune
                    </h4>

                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Lorsqu’une commune, collectivité ou structure associative
                      s’abonne grâce à votre recommandation, vous bénéficiez de
                      <strong> 10% de réduction sur votre prochain abonnement annuel</strong>.
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                      Une manière simple de soutenir la vie associative locale
                      tout en réduisant le coût de votre renouvellement.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}